import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsAPI } from '../../../api';
import { resolveUrl, quietBtn, fmtDate } from '../utils';

export const ClientDocuments = ({ clientId, t, lang, documents, allBoards, refresh, showToast }) => {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [linkingDoc, setLinkingDoc] = useState(null);
  const fileRef = useRef(null);

  const handleUpload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    for (const f of list) {
      try {
        await clientsAPI.documents.upload(clientId, f);
      } catch (e) {
        showToast(e.response?.data?.detail || (t.cs_upload_failed || 'Upload failed'));
      }
    }
    setUploading(false);
    refresh();
  };

  const handleRemove = async (doc) => {
    if (!window.confirm(t.cs_doc_remove_confirm || 'Remove this document permanently?')) return;
    try {
      await clientsAPI.documents.remove(clientId, doc.id);
      showToast(t.cs_deleted || 'Removed');
      refresh();
    } catch (e) { /* noop */ }
  };

  const handleLink = async (doc, boardId) => {
    try {
      await clientsAPI.documents.linkBoard(clientId, doc.id, boardId || null);
      setLinkingDoc(null);
      refresh();
      showToast(t.cs_saved || 'Saved');
    } catch (e) { /* noop */ }
  };

  const boardName = (id) => allBoards.find((b) => b.id === id)?.name;

  return (
    <section data-testid="client-documents-section">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
        <button className="btn-primary" data-testid="upload-client-doc-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
          {uploading ? '…' : (t.cs_upload_document || '+ Upload document')}
        </button>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" multiple hidden onChange={(e) => { handleUpload(e.target.files); e.target.value = ''; }} data-testid="client-doc-input" />
      </div>

      {documents.length === 0 ? (
        <div data-testid="client-docs-empty" style={{
          padding: '48px 24px', border: '1px dashed var(--kahla-border)', textAlign: 'center',
          fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
          fontSize: 15, color: 'var(--kahla-text-light)', lineHeight: 1.6,
        }}>
          {t.cs_docs_empty || 'Contracts, briefs, inspiration files — everything about this client, in one calm place.'}
        </div>
      ) : (
        <div style={{ maxWidth: 860 }}>
          {documents.map((doc) => (
            <div key={doc.id} data-testid={`client-doc-${doc.id}`} style={{ padding: '16px 4px', borderBottom: '1px solid var(--kahla-border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <p style={{ fontFamily: 'Playfair Display, serif', fontSize: 15.5, color: 'var(--kahla-charcoal)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {doc.title}
                  </p>
                  <p style={{ fontSize: 11.5, color: 'var(--kahla-text-light)', margin: '3px 0 0', letterSpacing: '0.04em' }}>
                    {(doc.file_type || '').toUpperCase()}{doc.file_size_display ? ` · ${doc.file_size_display}` : ''} · {fmtDate(doc.created_at, lang)}
                    {doc.board_id && boardName(doc.board_id) && (
                      <> · <button type="button" onClick={() => navigate(`/editor/${doc.board_id}`)} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11.5, color: '#cf9a3d', fontFamily: 'inherit' }}>{boardName(doc.board_id)}</button></>
                    )}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 16, flexShrink: 0, alignItems: 'baseline' }}>
                  <a href={resolveUrl(doc.file_url)} target="_blank" rel="noopener noreferrer" style={{ ...quietBtn, textDecoration: 'none', borderBottom: '1px solid var(--kahla-border)', paddingBottom: 2 }} data-testid={`doc-preview-${doc.id}`}>
                    {t.cs_preview || 'Preview'}
                  </a>
                  <a href={resolveUrl(doc.file_url)} download={doc.original_filename || doc.title} style={{ ...quietBtn, textDecoration: 'none', borderBottom: '1px solid var(--kahla-border)', paddingBottom: 2 }} data-testid={`doc-download-${doc.id}`}>
                    {t.cs_download || 'Download'}
                  </a>
                  <button type="button" style={{ ...quietBtn, borderBottom: '1px solid var(--kahla-border)', paddingBottom: 2 }} onClick={() => setLinkingDoc(linkingDoc === doc.id ? null : doc.id)} data-testid={`doc-link-${doc.id}`}>
                    {doc.board_id ? (t.cs_unlink_relabel || 'Board link') : (t.cs_link_to_board || 'Link to board')}
                  </button>
                  <button type="button" style={{ ...quietBtn, color: '#b0483a', borderBottom: '1px solid transparent', paddingBottom: 2 }} onClick={() => handleRemove(doc)} data-testid={`doc-remove-${doc.id}`}>
                    {t.cs_remove || 'Remove'}
                  </button>
                </div>
              </div>
              {linkingDoc === doc.id && (
                <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center' }} data-testid={`doc-link-picker-${doc.id}`}>
                  <select
                    defaultValue={doc.board_id || ''}
                    onChange={(e) => handleLink(doc, e.target.value)}
                    style={{ padding: '7px 10px', border: '1px solid var(--kahla-border)', background: '#fff', fontFamily: 'inherit', fontSize: 13, color: 'var(--kahla-charcoal)', maxWidth: 320 }}
                  >
                    <option value="">{t.cs_none || 'None'}</option>
                    {allBoards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ClientDocuments;
